import { gql, useMutation, useQuery } from '@apollo/client';
import IconClipboard from '@apollo/icons/default/IconClipboard.svg';
import IconOutlink from '@apollo/icons/small/IconOutlink.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  Code,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { Switch } from '@apollo/space-kit/Switch';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { useEffect, useMemo, useState } from 'react';

import { UserRoleSelect } from 'src/app/account/membersPage/roleSelect/UserRoleSelect';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  ThemeName,
  ThemeProvider,
} from 'src/components/themeProvider/ThemeProvider';
import { TooltipWithMutableContent } from 'src/components/TooltipWithMutableContent';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { UserRoleOption } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

import { GraphRef, graphRefToString } from '../../hooks/useGraphRef';
import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { CollectionEntryState } from '../hooks/useExplorerState/useEditorTabState/collectionEntryState';
import { headersObjectFromDefinitions } from '../hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';

import { CDNSnippet } from './CDNSnippet';
import { ReactNpmSnippet } from './ReactNpmSnippet';
import { VanillaNpmSnippet } from './VanillaNpmSnippet';

const tabs: Record<'react' | 'vanilla' | 'cdn', string> = {
  react: 'React',
  vanilla: 'Javascript',
  cdn: 'Built file on CDN',
};

export const EmbedExplorerModal = ({
  isOpen,
  onClose,
  graphRef,
  defaultTabState,
  accountId,
}: {
  isOpen: boolean;
  onClose: () => void;
  graphRef: GraphRef;
  defaultTabState: CollectionEntryState | null;
  accountId: string | undefined;
}) => {
  const { currentExplorerRouteConfig } = useInternalGraphLinking();
  const { embedModalTabState } = useRouteParams(currentExplorerRouteConfig);
  const {
    document: embedDocument,
    variables,
    headers,
  } = embedModalTabState || {};

  const [persistChangesInEmbedConfig, setPersistChangesInEmbedConfig] =
    useState(false);
  const [collapseDocsPanelInEmbedConfig, setCollapseDocsPanelInEmbedConfig] =
    useState(false);
  const [
    hideHeadersAndEnvVarsInEmbedConfig,
    setHideHeadersAndEnvVarsInEmbedConfig,
  ] = useState(false);
  const [darkModeThemeInEmbedConfig, setDarkModeThemeInEmbedConfig] =
    useState(false);

  const [
    showCodeSnippetCopiedConfirmation,
    setShowCodeSnippetCopiedConfirmation,
  ] = useState(false);
  const [includeAutoInviteInEmbedConfig, setIncludeAutoInviteInEmbedConfig] =
    useState(false);

  useEffect(() => {
    if (showCodeSnippetCopiedConfirmation) {
      const timeout = setTimeout(() => {
        setShowCodeSnippetCopiedConfirmation(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [showCodeSnippetCopiedConfirmation]);

  const { data, error, loading } = useQuery<
    GraphQLTypes.RegisteredGraphEndpointsQuery,
    GraphQLTypes.RegisteredGraphEndpointsQueryVariables
  >(
    gql`
      query RegisteredGraphEndpointsQuery(
        $graphId: ID!
        $graphVariant: String!
      ) {
        service(id: $graphId) {
          id
          schemaTag(tag: $graphVariant) {
            id
            variant {
              id
              url
              isPublic
            }
          }
          account {
            id
            availableRoles
            roles {
              canManageMembers
            }
          }
        }
        me {
          id
          ... on User {
            type
          }
        }
      }
    `,
    {
      variables: {
        graphId: graphRef?.graphId as string,
        graphVariant: graphRef?.graphVariant as string,
      },
      skip: !graphRef,
    },
  );

  const shouldPrefillEmbedConfigWithTabState = !!(
    embedDocument ||
    headers ||
    variables
  );

  const [currentStaticInvitationRole, setCurrentStaticInvitationRole] =
    useState<UserRoleOption>(GraphQLTypes.UserPermission.CONSUMER);

  const [inviteToken, setInviteToken] = useState<string | undefined>();

  const [
    createStaticInvitation,
    {
      loading: createStaticInvitationLoading,
      error: createStaticInvitationMutationError,
    },
  ] = useMutation<
    GraphQLTypes.CreateEmbedInvitation,
    GraphQLTypes.CreateEmbedInvitationVariables
  >(gql`
    mutation CreateEmbedInvitation(
      $accountId: ID!
      $currentStaticInvitationRole: UserPermission!
    ) {
      account(id: $accountId) {
        createStaticInvitation(role: $currentStaticInvitationRole) {
          joinToken
        }
      }
    }
  `);

  const [tab, setTab] = useState<keyof typeof tabs>('react');

  const graphRefString = graphRefToString(graphRef) || '<graphId@variant>';

  const snippetContent = useMemo(() => {
    if (!defaultTabState) return null;
    const defaultHeadersString = defaultTabState.headers
      ? JSON.stringify(headersObjectFromDefinitions(defaultTabState.headers))
      : undefined;
    const embedCodeSnippetProps = {
      endpointUrl: data?.service?.schemaTag?.variant.url
        ? data.service.schemaTag.variant.url
        : 'loading',
      persistChangesInEmbedConfig,
      hideHeadersAndEnvVarsInEmbedConfig,
      document: shouldPrefillEmbedConfigWithTabState
        ? embedDocument || ''
        : defaultTabState.operation || '',
      headers:
        shouldPrefillEmbedConfigWithTabState && headers
          ? headers
          : (defaultHeadersString ?? `{}`),
      variables:
        shouldPrefillEmbedConfigWithTabState && variables?.length
          ? variables
          : (defaultTabState.variables ?? '{}'),
      docsPanelState: collapseDocsPanelInEmbedConfig ? 'closed' : 'open',
      theme: darkModeThemeInEmbedConfig ? 'dark' : 'light',
      graphRefString,
      ...(includeAutoInviteInEmbedConfig &&
        accountId &&
        inviteToken && {
          autoInviteOptions: {
            inviteToken,
            accountId,
          },
        }),
    };
    switch (tab) {
      case 'react':
        return <ReactNpmSnippet {...embedCodeSnippetProps} />;
      case 'vanilla':
        return <VanillaNpmSnippet {...embedCodeSnippetProps} />;
      case 'cdn':
        return <CDNSnippet {...embedCodeSnippetProps} />;
      default:
        assertUnreachable(tab);
    }
  }, [
    collapseDocsPanelInEmbedConfig,
    darkModeThemeInEmbedConfig,
    data,
    defaultTabState,
    embedDocument,
    graphRefString,
    headers,
    hideHeadersAndEnvVarsInEmbedConfig,
    persistChangesInEmbedConfig,
    shouldPrefillEmbedConfigWithTabState,
    tab,
    variables,
    includeAutoInviteInEmbedConfig,
    inviteToken,
    accountId,
  ]);

  const canCreateInvite = !!(
    data?.service?.account?.roles?.canManageMembers && accountId
  );

  useEffect(() => {
    const createInviteToken = async () => {
      if (accountId) {
        const inviteData = await createStaticInvitation({
          variables: {
            accountId,
            currentStaticInvitationRole,
          },
        });
        setInviteToken(
          inviteData.data?.account?.createStaticInvitation?.joinToken,
        );
      }
    };

    if (canCreateInvite) {
      createInviteToken();
    }
  }, [
    createStaticInvitation,
    data,
    currentStaticInvitationRole,
    canCreateInvite,
    accountId,
  ]);

  const isSSO = data?.me?.__typename === 'User' && data.me.type === 'SSO';
  const canViewAutoInviteToggle =
    !data?.service?.schemaTag?.variant.isPublic && !isSSO;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Embed Explorer</ModalTitle>
          <ModalDescription>
            {tab === 'react' || tab === 'vanilla' ? (
              <>
                <TooltipWithMutableContent
                  initialContent="Copy"
                  shouldWrapChildren={true}
                >
                  {({ setContent, resetContent }) => (
                    <Button
                      variant="hidden"
                      size="sm"
                      isDisabled={createStaticInvitationLoading}
                      className="group h-auto p-0"
                      onClick={() => {
                        copy('npm i @apollo/explorer');
                        setContent('Copied');
                      }}
                      onMouseEnter={() => resetContent()}
                    >
                      <Code className="group-hover:bg-btn-secondary-hover group-active:bg-btn-secondary-selected">
                        npm i @apollo/explorer
                      </Code>
                    </Button>
                  )}
                </TooltipWithMutableContent>
                , then paste this code snippet to embed{' '}
              </>
            ) : (
              <>Copy and paste this code snippet to embed </>
            )}
            {graphRef ? <>{graphRefToString(graphRef)}'s</> : 'this'} Explorer
            on a webpage.{' '}
            <ClickableText
              className="text-link"
              target="_blank"
              href={DocsRouter.path('EmbeddedExplorer')}
              as="a"
            >
              Learn more.
            </ClickableText>
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div style={{ height: 440 }}>
              <Loading />
            </div>
          ) : !data ? (
            <Alert status="error">
              {error?.message ?? 'Something went wrong.'}
            </Alert>
          ) : (
            <div className="flex">
              <div className="w-1/3 text-sm text-secondary">
                <Heading as="h3" variant="title">
                  Display Options
                </Heading>
                <Switch
                  className="mt-2 font-normal"
                  isSelected={darkModeThemeInEmbedConfig}
                  onChange={() =>
                    setDarkModeThemeInEmbedConfig((current) => !current)
                  }
                >
                  Dark mode
                </Switch>
                <Switch
                  className="mt-2 font-normal"
                  isSelected={collapseDocsPanelInEmbedConfig}
                  onChange={() =>
                    setCollapseDocsPanelInEmbedConfig((current) => !current)
                  }
                >
                  Collapse docs panel
                </Switch>
                <Switch
                  className="mt-2 font-normal"
                  isSelected={hideHeadersAndEnvVarsInEmbedConfig}
                  onChange={() =>
                    setHideHeadersAndEnvVarsInEmbedConfig((current) => !current)
                  }
                >
                  Hide Headers and Environment Variables
                </Switch>
                <Heading as="h3" variant="title" className="mt-4">
                  Persist client-side state between page loads
                </Heading>
                <Switch
                  className="mb-4 mt-2 text-sm font-normal text-secondary"
                  isSelected={persistChangesInEmbedConfig}
                  onChange={() =>
                    setPersistChangesInEmbedConfig((current) => !current)
                  }
                >
                  Save client-side changes that a user makes in embedded
                  Explorer <br /> between page loads (via local storage).
                </Switch>
                {canViewAutoInviteToggle && (
                  <>
                    <Heading as="h3" variant="title" className="mt-4">
                      Invite users to your Studio org
                    </Heading>

                    <Tooltip
                      disabled={canCreateInvite}
                      label="You don't have permission to invite members to your organization. Contact an Org Admin to set this up."
                      placement="bottom"
                    >
                      <div>
                        <Switch
                          className="my-2 text-sm font-normal text-secondary"
                          isSelected={includeAutoInviteInEmbedConfig}
                          isDisabled={!canCreateInvite}
                          onChange={() => {
                            setIncludeAutoInviteInEmbedConfig(
                              (current) => !current,
                            );
                          }}
                        >
                          Users who authenticate will be automatically invited
                          to your org with the selected role
                        </Switch>
                      </div>
                    </Tooltip>
                    {includeAutoInviteInEmbedConfig && (
                      <ThemeProvider
                        // TODO(dark-mode) remove override when dark mode is globally ready
                        overrideThemeName={ThemeName.LIGHT}
                      >
                        <UserRoleSelect
                          disabled={createStaticInvitationLoading}
                          size="small"
                          target="user-in-org"
                          className="mt-2"
                          userRole={currentStaticInvitationRole}
                          setUserRole={(newRole) => {
                            setCurrentStaticInvitationRole(newRole);
                          }}
                          availableRoles={
                            data.service?.account?.availableRoles ?? []
                          }
                        />
                        {createStaticInvitationMutationError && (
                          <ErrorMessage
                            className="mt-2"
                            error={createStaticInvitationMutationError}
                          />
                        )}
                      </ThemeProvider>
                    )}
                  </>
                )}
              </div>
              <div className="ml-4 w-2/3 pl-4">
                <ThemeProvider
                  // TODO(dark-mode) remove override when dark mode is globally ready
                  overrideThemeName={ThemeName.LIGHT}
                >
                  <div role="tablist" className="relative z-10 flex gap-2">
                    {Object.entries(tabs).map(([key, value]) => (
                      <Button
                        key={key}
                        size="sm"
                        variant="hidden"
                        role="tab"
                        onClick={() => setTab(key as keyof typeof tabs)}
                        className={classnames(
                          tab === key
                            ? 'border-b-transparent bg-primary text-primary'
                            : 'border-b-primary bg-disabled text-secondary hover:text-black',
                          'h-8 rounded-b-none rounded-t border border-primary py-1 text-base font-normal',
                        )}
                      >
                        {value}
                      </Button>
                    ))}
                  </div>
                </ThemeProvider>
                <div className="relative -mt-px mb-2 overflow-auto rounded-b border border-primary bg-primary py-2">
                  <TooltipWithMutableContent initialContent="Copy code snippet">
                    {({ setContent, resetContent }) => (
                      <div className="sticky top-0 -mt-7 ml-auto mr-2 w-7">
                        <IconButton
                          aria-label={`Copy ${tab} codeblock`}
                          size="sm"
                          variant="hidden"
                          isDisabled={createStaticInvitationLoading}
                          icon={<IconClipboard />}
                          onClick={() => {
                            copy(
                              window.document?.getElementById?.(
                                'embed-code-snippet-content',
                              )?.innerText || '',
                            );
                            setContent('Code snippet copied ✨');
                          }}
                          onMouseEnter={() => resetContent()}
                          data-analytics-label={`Embeddable Explorer: Copy ${tab} codeblock`}
                          data-analytics-category="Embeddable Explorer"
                        />
                      </div>
                    )}
                  </TooltipWithMutableContent>
                  <pre
                    id="embed-code-snippet-content"
                    className="max-h-96 whitespace-pre-wrap pl-4 text-sm text-neutral"
                  >
                    {snippetContent}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <ClickableText
            className="text-link"
            target="_blank"
            href={DocsRouter.path('EmbeddedExplorerOptions')}
            as="a"
          >
            More configuration options
            <IconOutlink className="ml-1" />
          </ClickableText>
          <ButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Tooltip
              disabled={!showCodeSnippetCopiedConfirmation}
              label="Code snippet copied ✨"
            >
              <Button
                variant="primary"
                type="button"
                data-analytics-label="Embed Explorer Modal: Copied code snippet"
                data-analytics-category="Embeddable Explorer"
                onClick={() => {
                  copy(
                    window.document?.getElementById?.(
                      'embed-code-snippet-content',
                    )?.innerText || '',
                  );
                  setShowCodeSnippetCopiedConfirmation(true);
                }}
                isDisabled={
                  showCodeSnippetCopiedConfirmation ||
                  createStaticInvitationLoading
                }
              >
                {showCodeSnippetCopiedConfirmation ? 'Copied' : 'Copy'}
              </Button>
            </Tooltip>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
